import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { linkChiama, linkWhatsApp } from '../../utils/percorso';
import { LINGUE } from '../../utils/scheda';
import { contesto, valida } from '../../i18n';
import Icona from '../../components/cliente/Icone';
import { Iniziale, usePersone } from '../../components/staff/Persone';
import TabPanoramica from '../../components/scheda/TabPanoramica';
import TabImmobili from '../../components/scheda/TabImmobili';
import TabVisite from '../../components/scheda/TabVisite';
import TabProposte from '../../components/scheda/TabProposte';
import TabDocumenti, { TestaDocumenti } from '../../components/scheda/TabDocumenti';
import TabCronologia from '../../components/scheda/TabCronologia';
import { AggiungiLink, FaiFirmare, Messaggio, ModificaRicerca, NuovaProposta } from '../../components/scheda/Moduli';
import FissaVisita from '../../components/staff/FissaVisita';
import EsitoTelefonata from '../../components/staff/EsitoTelefonata';
import Eliminazione from '../../components/staff/Eliminazione';
import ModificaDati from '../../components/scheda/ModificaDati';
import '../../components/staff/scheda.css';

// /admin/clienti/:id: la scheda cliente dello staff, bozza design/admin-cliente-v2.html. Testa con iniziale, contatti,
// città, "cliente da", chi segue (cambia), lingua, fase; WhatsApp, Chiama, Modifica, "+ Azione". Una scheda = un
// componente in components/scheda/: Panoramica · Immobili · Visite · Proposte · Documenti · Note e cronologia
// (#immobili, #visite, … nell'indirizzo). Il collaboratore apre solo i suoi clienti (il server risponde 403).

const SCHEDE = ['panoramica', 'immobili', 'visite', 'proposte', 'documenti', 'cronologia'];
const FASI = ['Ricerca', 'Visita', 'Proposta', 'Mutuo', 'Rogito'];

// Il permesso di soggiorno nella testa della scheda: si legge com'è davvero (backend/permesso_soggiorno.py).
// Prima diceva "in scadenza" per tutti — anche per un lungo periodo UE, che non scade, e per un rinnovo già in
// corso. "senza_scadenza" non arriva nemmeno (il server non lo manda): non c'è niente da dire.
const FRASE_PERMESSO = {
  valido: 'permesso valido fino al',
  in_scadenza: 'permesso in scadenza il',
  scaduto: 'permesso scaduto il',
};
export const testoPermesso = (permesso) => {
  if (!permesso?.scadenza) return null;
  if (permesso.stato === 'in_rinnovo') return 'permesso in rinnovo · ricevuta caricata';
  const frase = FRASE_PERMESSO[permesso.stato];
  if (!frase) return null;
  return `${frase} ${new Date(permesso.scadenza).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}`;
};

// "+ Azione": proponi immobile, chiedi documenti, fissa visita, nuova proposta, nota, messaggio, elimina cliente.
// Nuova proposta, messaggio ed elimina solo per l'admin (come le rotte del server).
// «Invia guida per installare l'app»: un WhatsApp già scritto, nella lingua del cliente, con il link a
// /installa — la pagina che riconosce da sola se ha un iPhone o un Android (frontend/src/pages/Installa.jsx).
// Lo staff lavora in italiano, il cliente legge nella sua lingua: per questo il testo si prende da `contesto(lingua)`
// e non dalle traduzioni della pagina.
export const messaggioGuidaApp = (cliente) => {
  const lingua = valida(cliente?.lingua);
  const link = `${window.location.origin}/installa${lingua === 'it' ? '' : `?lingua=${lingua}`}`;
  return contesto(lingua).t('installa.whatsapp', { nome: cliente?.nome || '', link });
};

export const linkGuidaApp = (cliente) => {
  const wa = linkWhatsApp(cliente?.telefono);
  return wa ? `${wa}?text=${encodeURIComponent(messaggioGuidaApp(cliente))}` : null;
};

const MenuAzione = ({ apri, vaiA, isAdmin, onElimina, guidaApp }) => {
  const [aperto, setAperto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aperto) return undefined;
    const fuori = (e) => { if (ref.current && !ref.current.contains(e.target)) setAperto(false); };
    const esc = (e) => { if (e.key === 'Escape') setAperto(false); };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', esc); };
  }, [aperto]);
  const voce = (testo, fn) => <button type="button" role="menuitem" onClick={() => { setAperto(false); fn(); }}>{testo}</button>;
  return (
    <div className="menu-az" ref={ref}>
      <button type="button" className="btn p" onClick={() => setAperto(!aperto)} aria-expanded={aperto} aria-haspopup="menu">+ Azione</button>
      {aperto && (
        <div className="tendina" role="menu">
          {voce('Proponi un immobile', () => apri('link'))}
          {voce('Chiedi documenti', () => vaiA('documenti'))}
          {voce('Fissa una visita', () => apri('visita'))}
          {isAdmin && voce('Nuova proposta', () => apri('proposta'))}
          {voce('Aggiungi una nota', () => apri('nota'))}
          {guidaApp && (
            <a role="menuitem" href={guidaApp} target="_blank" rel="noopener noreferrer"
              onClick={() => setAperto(false)} data-testid="guida-app-whatsapp">Invia guida per installare l'app</a>
          )}
          {isAdmin && voce('Messaggio al cliente', () => apri('messaggio'))}
          {isAdmin && <button type="button" role="menuitem" className="rosso" onClick={() => { setAperto(false); onElimina(); }}>Elimina il cliente…</button>}
        </div>
      )}
    </div>
  );
};

const ChiSegue = ({ scheda, onFatto }) => {
  const { persone } = usePersone();
  const [cambia, setCambia] = useState(false);
  const scegli = async (id) => {
    try { await axios.put(`${API}/clienti/${scheda.cliente.id}/consulente`, { consulente_id: id }); toast.success('Fatto'); setCambia(false); onFatto(); } catch { toast.error('Non riuscito'); }
  };
  return (
    <span className="who">
      <Iniziale persona={scheda.segue} />segue {scheda.segue?.nome || '—'}
      {scheda.puo_cambiare_chi_segue && !cambia && <button type="button" className="link" onClick={() => setCambia(true)}>cambia</button>}
      {cambia && (
        <select defaultValue={scheda.segue?.id} onChange={(e) => scegli(e.target.value)} aria-label="Chi segue il cliente">
          {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
      )}
    </span>
  );
};

const Lingua = ({ scheda, onFatto }) => {
  const cambia = async (lingua) => {
    try { await axios.put(`${API}/clienti/${scheda.cliente.id}`, { lingua }); onFatto(); } catch { toast.error('Non riuscito'); }
  };
  return (
    <span>parla <select value={scheda.cliente.lingua} onChange={(e) => cambia(e.target.value)} aria-label="Lingua del cliente">
      {LINGUE.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
    </select></span>
  );
};


// «Reinvia invito»: l'email con il link e il codice di collegamento, con la data dell'ultimo invio.
// Compare solo per chi ne ha ancora bisogno: pre-registrato e senza password.
const Invito = ({ c, onFatto }) => {
  const [attesa, setAttesa] = useState(false);
  const manda = async () => {
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/clienti/${c.id}/invito`);
      toast.success(`Invito mandato a ${r.data.email}`);
      onFatto();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invito non mandato. Riprovate.');
    } finally { setAttesa(false); }
  };
  if (!c.ha_email) {
    return <span className="muto" data-testid="invito-senza-email">niente email: il codice si dà a voce</span>;
  }
  return (
    <span data-testid="invito">
      {c.invito_mandato_il
        ? <>invito mandato il {quandoBreveInvito(c.invito_mandato_il)}{c.invito_mandato_da ? ` da ${c.invito_mandato_da}` : ''} · </>
        : <><b style={{ color: 'var(--warn)' }}>invito mai mandato</b> · </>}
      <button type="button" className="link" disabled={attesa} onClick={manda} data-testid="reinvia-invito">
        {c.invito_mandato_il ? 'Reinvia invito' : 'Manda invito'}
      </button>
    </span>
  );
};

// «28 set»: la data dell'invito, corta, nell'ora di Roma (le date in archivio sono in UTC)
export const quandoBreveInvito = (iso) => {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', timeZone: 'Europe/Rome' });
};


// «mai entrato» / «entrato 12 volte · ultima volta il 1 ott con Face ID»: a cosa serve l'app se nessuno la apre.
export const Ingressi = ({ c }) => {
  if (!c.ingressi) {
    return <span data-testid="ingressi"><b style={{ color: 'var(--warn)' }}>mai entrato nell&apos;app</b></span>;
  }
  const quante = c.ingressi === 1 ? 'entrato una volta' : `entrato ${c.ingressi} volte`;
  const quando = quandoBreveInvito(c.ultimo_ingresso);
  const come = c.ultimo_ingresso_modo ? ` con ${c.ultimo_ingresso_modo}` : '';
  return (
    <span data-testid="ingressi" title={c.primo_ingresso ? `la prima volta il ${quandoBreveInvito(c.primo_ingresso)}` : undefined}>
      {quante}{quando ? ` · ultima volta il ${quando}${come}` : ''}
    </span>
  );
};

const SchedaCliente = () => {
  const { id } = useParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const { ioId } = usePersone();
  const isAdmin = userType === 'admin';
  const daHash = (h) => { const k = h.replace('#', ''); return ['archivio', 'annunci'].includes(k) ? 'immobili' : SCHEDE.includes(k) ? k : null; };
  const [tab, setTab] = useState(daHash(hash) || 'panoramica');
  const [scheda, setScheda] = useState(null);
  const [immobili, setImmobili] = useState([]);
  const [visite, setVisite] = useState([]);
  const [proposte, setProposte] = useState([]);
  const [modulo, setModulo] = useState(null);
  // il gruppo di case della stessa agenzia da chiamare in una volta (backend/agenzia_case.py)
  const [gruppo, setGruppo] = useState(null);
  const [elimina, setElimina] = useState(false);
  const [modifica, setModifica] = useState(false);
  const [versione, setVersione] = useState(0);
  const ricarica = useCallback(() => setVersione((v) => v + 1), []);

  useEffect(() => { if (daHash(hash)) setTab(daHash(hash)); }, [hash]);  // #archivio: le case in archivio, nella scheda Immobili
  useEffect(() => {
    // I tre casi si tengono separati, come nella scheda dell'immobile: un 500 o la rete caduta NON vogliono dire
    // che il cliente non c'è. Prima finiva tutto in «Cliente non trovato» e si andava a cercare nel cestino un
    // cliente che stava benissimo (blocco 3, secondo giro).
    axios.get(`${API}/clienti/${id}/scheda`).then((r) => setScheda(r.data))
      .catch((e) => setScheda(e.response?.status === 403 ? 'vietato' : e.response?.status === 404 ? false : 'errore'));
    axios.get(`${API}/clienti/${id}/immobili`).then((r) => setImmobili(r.data)).catch(() => {});
    axios.get(`${API}/clienti/${id}/visite`).then((r) => setVisite(r.data)).catch(() => {});
    axios.get(`${API}/clienti/${id}/proposte`).then((r) => setProposte(r.data)).catch(() => {});
  }, [id, versione]);

  const vaiA = (t) => {
    setTab(t);
    if (t !== 'cronologia') setModulo((m) => (m?.tipo === 'nota' ? null : m));
    navigate(`#${t === 'panoramica' ? '' : t}`, { replace: true });
    window.scrollTo({ top: 0 });
  };
  // la nota si scrive nella scheda "Note e cronologia"; gli altri moduli si aprono sopra la scheda corrente
  const apri = (tipo, immobileId) => {
    if (tipo === 'nota') { setModulo({ tipo }); vaiA('cronologia'); return; }
    setModulo({ tipo, immobileId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const fatto = () => { setModulo(null); ricarica(); };

  // Le tre schermate che non sono la scheda. Come per l'immobile (pages/staff/SchedaImmobile.jsx): si dice cos'è
  // successo, si dice dove guardare se il cliente è stato eliminato, e c'è comunque un modo per tornare indietro —
  // prima da "Cliente non trovato." non si andava da nessuna parte (blocco 3, primo giro).
  if (scheda === 'vietato' || scheda === 'errore' || scheda === false || !scheda) {
    return (
      <main className="st wrap">
        {scheda !== null && (
          <button type="button" className="back link-back" onClick={() => navigate(-1)} data-testid="scheda-indietro">
            <Icona nome="indietro" />Indietro
          </button>
        )}
        <p className="vuoto" role="status">
          {scheda === 'vietato' && 'Questo cliente non è tra quelli che seguite.'}
          {scheda === false && 'Cliente non trovato: forse è stato eliminato (Impostazioni → Cestino).'}
          {scheda === 'errore' && 'Non riesco a caricare la scheda. Riprovate tra poco.'}
          {!scheda && 'Carico la scheda…'}
        </p>
        {scheda === 'errore' && (
          <p className="vuoto"><button type="button" className="btn sm" onClick={ricarica} data-testid="scheda-riprova">Riprovate</button></p>
        )}
      </main>
    );
  }
  const c = scheda.cliente;
  const n = scheda.conteggi;
  const wa = linkWhatsApp(c.telefono);
  const tel = linkChiama(c.telefono);
  const cliente = { id: c.id, nome: c.nome };
  const giorni = c.cliente_da ? new Date(c.cliente_da) : null;
  const chiudi = () => setModulo(null);

  return (
    <>
      <main className="st wrap" data-testid="scheda-cliente">
        <Link className="back" to="/admin/clienti"><Icona nome="indietro" />Clienti</Link>
        <div className="ch r">
          <div className="av">{c.iniziale}</div>
          <div style={{ minWidth: 0 }}>
            <h1>{c.nome_intero}</h1>
            <div className="meta">
              {c.telefono && <a href={tel || '#'}>{c.telefono}</a>}
              {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
              {c.citta && <span>{c.citta}</span>}
              {giorni && <span>cliente dal {giorni.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })} · {c.giorni} giorni</span>}
              <ChiSegue scheda={scheda} onFatto={ricarica} />
              <Lingua scheda={scheda} onFatto={ricarica} />
              {/* il permesso di soggiorno del nucleo: la stessa pagina che legge il cliente, con i passi per il rinnovo */}
              {testoPermesso(scheda.permesso) && (
                <Link to={`/rinnovo-permesso?cliente=${cliente.id}`} title="La procedura del rinnovo, come la legge il cliente" data-testid="permesso-scheda">
                  {testoPermesso(scheda.permesso)}
                </Link>
              )}
              {c.pre_registrato && c.codice_collegamento && <span>codice per registrarsi: <b style={{ color: 'var(--brass2)' }}>{c.codice_collegamento}</b></span>}
              {/* L'invito con il link e il codice partiva **solo** trasformando un contatto in cliente: un cliente
                  creato a mano non lo riceveva, e non c'era nessun posto da cui mandarlo. In produzione cinque
                  pre-registrati di aprile sono rimasti senza, e nessuno poteva accorgersene. Qui si vede se è
                  partito, quando e per mano di chi (backend/contatti.py, `reinvia_invito`). */}
              {c.serve_invito && <Invito c={c} onFatto={ricarica} />}
              {/* Usa davvero l'app? Prima non si poteva sapere: un accesso con la password non lasciava nessuna
                  traccia, e per i clienti non c'era nessun campo (backend/server.py, `segna_ingresso`) */}
              <Ingressi c={c} />
            </div>
            <div className="pipe" aria-hidden="true">{FASI.map((f, i) => <i key={f} className={i + 1 < scheda.fase.numero ? 'd' : i + 1 === scheda.fase.numero ? 'c' : undefined} />)}</div>
            <div className="pipe-l">{FASI.map((f, i) => (i + 1 === scheda.fase.numero ? <b key={f}>{f}</b> : <span key={f}>{f}</span>))}</div>
          </div>
          <div className="acts">
            {wa && <a className="btn wa" href={wa} target="_blank" rel="noopener noreferrer"><Icona nome="whatsapp" />WhatsApp</a>}
            {tel && <a className="btn" href={tel}>Chiama</a>}
            <button type="button" className="btn" onClick={() => setModifica(true)} aria-label="Modifica i dati del cliente">Modifica</button>
            <MenuAzione apri={apri} vaiA={vaiA} isAdmin={isAdmin} onElimina={() => setElimina(true)} guidaApp={linkGuidaApp(c)} />
          </div>
        </div>

        <div className="tabs r" role="tablist">
          {[['panoramica', 'Panoramica'], ['immobili', 'Immobili', n.immobili], ['visite', 'Visite', n.visite], ['proposte', 'Proposte', n.proposte],
            ['documenti', 'Documenti', n.documenti_mancanti ? `${n.documenti_mancanti} mancanti` : null], ['cronologia', 'Note e cronologia']].map(([k, nome, conta]) => (
            <button type="button" role="tab" key={k} aria-selected={tab === k} className={tab === k ? 'on' : undefined} onClick={() => vaiA(k)}>
              {nome}{conta ? <b>{conta}</b> : null}
            </button>
          ))}
        </div>

        {/* «Chiama per 3 case»: una telefonata sola per tutte le case che il cliente ha con quell'agenzia,
            un esito per riga (docs/piu-case-agenzia.md) */}
        {gruppo && (
          <section className="card r" data-testid="telefonata-gruppo">
            <div className="ttl">
              <h2>Telefonata a {gruppo.etichetta} <small>{gruppo.attive} case di {c.nome}</small></h2>
              {/* le stesse case si possono anche vedere tutte nello stesso pomeriggio, una dopo l'altra */}
              <button type="button" className="btn sm" data-testid="visite-in-fila"
                onClick={() => { setModulo({ tipo: 'visita', sequenza: gruppo.case }); setGruppo(null); }}>
                Visite in fila
              </button>
              <button type="button" className="btn sm" onClick={() => setGruppo(null)}>Chiudi</button>
            </div>
            <EsitoTelefonata
              voce={{ id: `agenzia:${gruppo.agenzia_id || gruppo.etichetta}`, tipo: 'telefonata', cliente_id: cliente.id,
                agenzia_id: gruppo.agenzia_id, cliente_breve: c.nome }}
              case={gruppo.case} nomeAgenzia={gruppo.etichetta} nomeCliente={c.nome} isAdmin={isAdmin}
              onFatto={() => { setGruppo(null); ricarica(); }} />
          </section>
        )}
        {modulo?.tipo === 'visita' && <FissaVisita clienteId={cliente.id} clienteNome={c.nome_intero || c.nome} immobili={immobili.filter((i) => i.stato_attivita !== 'scartato')}
          immobileId={modulo.immobileId} caseInSequenza={modulo.sequenza} onChiudi={chiudi} onFatto={fatto} />}
        {modulo?.tipo === 'proposta' && <NuovaProposta cliente={cliente} immobili={immobili} immobileId={modulo.immobileId} chiudi={chiudi} onFatto={fatto} />}
        {modulo?.tipo === 'link' && <AggiungiLink cliente={cliente} chiudi={chiudi} onFatto={fatto} />}
        {modulo?.tipo === 'messaggio' && <Messaggio cliente={cliente} chiudi={chiudi} onFatto={fatto} />}
        {modulo?.tipo === 'ricerca' && <ModificaRicerca cliente={cliente} chiudi={chiudi} onFatto={fatto} />}
        {modulo?.tipo === 'firma' && <FaiFirmare cliente={cliente} chiudi={chiudi} onFatto={ricarica} />}

        {tab === 'panoramica' && <TabPanoramica scheda={scheda} onCambia={ricarica} isAdmin={isAdmin} vaiA={vaiA} apri={apri} />}
        {tab === 'immobili' && <TabImmobili cliente={cliente} immobili={immobili} onFatto={ricarica} isAdmin={isAdmin} apri={apri} versione={versione}
          onChiamaGruppo={setGruppo} />}
        {tab === 'visite' && <TabVisite cliente={cliente} visite={visite} immobili={immobili} apri={apri} onFatto={ricarica} />}
        {tab === 'proposte' && <TabProposte proposte={proposte} onFatto={ricarica} isAdmin={isAdmin} apri={apri} />}
        {tab === 'documenti' && <TestaDocumenti apri={apri} />}
        {tab === 'cronologia' && <TabCronologia clienteId={id} note={scheda.note} onFatto={() => { setModulo(null); ricarica(); }}
          scriviNota={modulo?.tipo === 'nota'} versione={versione} />}
      </main>
      {tab === 'documenti' && <TabDocumenti clienteId={id} />}
      {modifica && <ModificaDati cliente={c} puoEmail={isAdmin || scheda.segue?.id === ioId} puoPassword={isAdmin} onChiudi={() => setModifica(false)}
        onFatto={() => { setModifica(false); ricarica(); }} />}
      {elimina && <Eliminazione tipo="cliente" id={id} onChiudi={() => setElimina(false)} onEliminato={() => navigate('/admin/clienti')} />}
    </>
  );
};

export default SchedaCliente;
